import Dialog from "../../../common/Dialog";
import { StatusBadge, relativeDate } from "../../../utils/Constant";

const DetailRow = ({ label, value, fullWidth = false }) => (
  <div className={`group flex min-w-0 flex-col gap-1 rounded-lg border border-slate-100 bg-white px-3 py-2.5 transition-all duration-200 hover:border-slate-200 hover:bg-slate-50/70 sm:px-4 ${ fullWidth ? "sm:col-span-2" : "" }`}>
    <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
      {label}
    </span>

    <span className="break-words text-[13px] font-semibold leading-5 text-slate-800 sm:text-[13.5px]">
      {value !== undefined && value !== null && value !== "" ? value : "—"}
    </span>
  </div>
);

const PriceCard = ({ label, value, highlight = false }) => (
  <div className={`flex flex-col gap-1 rounded-xl border px-3 py-3 sm:px-4 ${
      highlight ? "border-emerald-200 bg-emerald-50/70" : "border-slate-100 bg-slate-50/70"
    }`}
  >
    <span className={`text-[10px] font-semibold uppercase tracking-wider ${ highlight ? "text-emerald-600" : "text-slate-400"}`}>
      {label}
    </span>

    <span className={`break-words text-[15px] font-bold sm:text-base ${ highlight ? "text-emerald-700" : "text-slate-800"}`}>
      {value}
    </span>
  </div>
);

export default function RequestDetailModal({ item, onClose }) {
  if (!item) return null;

  const quantity = Number(item.numUnits) || 0;
  const unitPrice = Number(item.unitPrice) || 0;
  const total = quantity * unitPrice;

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(value || 0);

  return (
    <Dialog open={!!item} onClose={onClose} title="Request Details" subtitle={item.item} widthClass="w-[95vw] sm:max-w-xl lg:max-w-2xl" >
      <div className="max-h-[78vh] overflow-y-auto pr-0.5 sm:max-h-[80vh]">
        <div className="mb-4 rounded-xl border border-slate-100 bg-gradient-to-r from-slate-50 to-white p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400"> Requested Item </p>
              <h3 className="break-words text-[16px] font-bold leading-snug text-slate-900 sm:text-[18px]">
                {item.item || "—"}
              </h3>

              {item.catalog && (
                <p className="mt-1 break-words text-[12px] text-slate-500">
                  Catalog:{" "}
                  <span className="font-medium text-slate-600">
                    {item.catalog}
                  </span>
                </p>
              )}
            </div>

            <div className="shrink-0 self-start">
              <StatusBadge status={item.status} />
            </div>
          </div>
        </div>

        {/* Pricing summary */}
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
          <PriceCard label="Quantity" value={quantity || "—"} />
          <PriceCard label="Unit Price" value={formatCurrency(unitPrice)} />
          <div className="col-span-2 sm:col-span-1">
            <PriceCard label="Total Amount" value={formatCurrency(total)} highlight />
          </div>
        </div>

        {/* Item Information */}
        <section className="mb-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="h-4 w-1 rounded-full bg-slate-800" />
            <h4 className="text-[12px] font-bold uppercase tracking-wide text-slate-700">
              Item Information
            </h4>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <DetailRow label="Type" value={item.type} />
            <DetailRow label="Catalog" value={item.catalog} />
            <DetailRow label="Manufacturer" value={item.manufacturer} />
            <DetailRow label="Unit Size" value={item.unitSize} />
          </div>
        </section>

        {/* Request Information */}
        <section className="mb-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="h-4 w-1 rounded-full bg-slate-800" />

            <h4 className="text-[12px] font-bold uppercase tracking-wide text-slate-700">
              Request Information
            </h4>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <DetailRow label="Requested By" value={item.by} />
            <DetailRow label="Project" value={item.project} />
            <DetailRow label="PO Number" value={item.poNum} />
            <DetailRow label="Requested" value={item.date ? relativeDate(item.date) : "—"}/>
          </div>
        </section>

        {/* Notes */}
        {item.notes && (
          <section>
            <div className="mb-2 flex items-center gap-2">
              <div className="h-4 w-1 rounded-full bg-slate-800" />
              <h4 className="text-[12px] font-bold uppercase tracking-wide text-slate-700">
                Notes
              </h4>
            </div>

            <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-3 sm:p-4">
              <p className="whitespace-pre-wrap break-words text-[12.5px] leading-5 text-slate-600 sm:text-[13px]">
                {item.notes}
              </p>
            </div>
          </section>
        )}
      </div>
    </Dialog>
  );
}
